import {
  Anchor,
  Card,
  Group,
  Select,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSessionGroupsListQuery } from '../queries/useSessionGroupsListQuery';
import type { SessionFullTractMetrics } from '../hooks/useSessionFullTractMetrics';
import type { SpectralWorkspaceSession } from '../types';
import { AddedSessionsPanel } from './AddedSessionsPanel';

type WorkspaceSessionsSectionProps = {
  sessions: SpectralWorkspaceSession[];
  activeSessionGroupId: string | null;
  analysisId?: string;
  isLoadingSessions?: boolean;
  sessionMetrics?: SessionFullTractMetrics[];
  onActiveSessionGroupChange: (groupId: string | null) => void;
};

export function WorkspaceSessionsSection({
  sessions,
  activeSessionGroupId,
  analysisId,
  isLoadingSessions = false,
  sessionMetrics = [],
  onActiveSessionGroupChange,
}: WorkspaceSessionsSectionProps) {
  const {
    data: groups = [],
    isLoading: isLoadingGroups,
    error: groupsError,
  } = useSessionGroupsListQuery();

  const groupOptions = useMemo(
    () =>
      groups.map(group => ({
        value: group.groupId,
        label: `${group.name} (${group.sessionCount} sessioni)`,
      })),
    [groups]
  );

  const groupsErrorMessage =
    groupsError instanceof Error
      ? groupsError.message
      : groupsError
        ? 'Impossibile caricare i gruppi di sessioni.'
        : null;

  return (
    <Stack gap='md'>
      <Card withBorder>
        <Title order={4}>Gruppo sessioni attivo</Title>
        <Text c='dimmed' size='sm' mt={4}>
          Le sessioni del workspace derivano dal gruppo selezionato. Per
          analizzare o riparare una sessione, aprila dalla pagina gruppi.
        </Text>

        <Stack gap='xs' mt='md'>
          {isLoadingGroups ? (
            <Stack gap={6}>
              <Skeleton height={12} width={96} radius='sm' />
              <Skeleton height={36} radius='sm' />
            </Stack>
          ) : (
            <Select
              label='Gruppo attivo'
              placeholder='Seleziona un gruppo'
              data={groupOptions}
              value={activeSessionGroupId}
              onChange={value => onActiveSessionGroupChange(value)}
              clearable
              nothingFoundMessage='Nessun gruppo disponibile'
            />
          )}

          {groupsErrorMessage && (
            <Text size='sm' c='red'>
              {groupsErrorMessage}
            </Text>
          )}

          {!isLoadingGroups && groups.length === 0 && (
            <Text size='sm' c='dimmed'>
              Nessun gruppo disponibile. Crea un gruppo per collegare sessioni
              all&apos;analisi.
            </Text>
          )}

          {!activeSessionGroupId && !isLoadingGroups && (
            <Text size='sm' c='dimmed'>
              Nessun gruppo selezionato: l&apos;export CSV resterà disabilitato
              finché non scegli un gruppo con almeno una sessione.
            </Text>
          )}

          <Group gap='md'>
            <Anchor component={Link} to='/sessions/group' size='sm'>
              Gestisci gruppi
            </Anchor>
            <Anchor component={Link} to='/sessions/group/new' size='sm'>
              Crea gruppo
            </Anchor>
          </Group>
        </Stack>
      </Card>

      <AddedSessionsPanel
        sessions={sessions}
        activeSessionGroupId={activeSessionGroupId}
        analysisId={analysisId}
        isLoadingSessions={isLoadingSessions}
        sessionMetrics={sessionMetrics}
      />
    </Stack>
  );
}
